import { Component, Prop, Element, Event, EventEmitter, Host, h } from '@stencil/core';
import { setCSSProps, createNamespaceKey } from '../../utils/utils';


/**
 * Accordion Items are placed as children of the parent `cbp-accordion` tag. The content is slotted and the 
 * heading control text is provided via the label property or the optional named slot.
 * 
 * @slot - Accordion Item content is placed in the default slot.
 * @slot cbp-accordion-item-label - Optionally, an Accordion Item heading with markup may be placed in this slot rather than via the labal property.
 */
@Component({
  tag: 'cbp-accordion-item',
  styleUrl: 'cbp-accordion-item.scss',
})
export class CbpAccordionItem {
  private control: HTMLElement;
  private button: HTMLButtonElement;

  @Element() host: HTMLElement;

  /**
   * Specifies an optional `id` for the accordion item heading, also used to generate an `id` for 
   * the accordion item content wrapper. If this property is not specified, a unique string will 
   * automatically be generated. 
   */
  @Prop() headingId: string = createNamespaceKey('cbp-accordion-item');
  
  /** Specifies whether the accordion is open. */
  @Prop({ reflect: true }) open: boolean = false;
  
  /** The accordion control label. */
  @Prop() label: string;

  /** The heading level of the accordion item control. Defaults to h3. */
  @Prop() headingLevel: 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h3';
  
  /** Specifies an optional color variant of the accordion item. */
  @Prop({reflect:true}) color: 'danger';

  /** Supports adding inline styles as an object */
  @Prop() sx: any = {};

  /** A custom event emitted when the accordion item control is activated. */
  @Event() accordionItemClick: EventEmitter;
  handleClick() {
    this.open = !this.open;
    this.accordionItemClick.emit({
      host: this.host,
      button: this.button,
      open: this.open,
    });
    this.button.focus();
  }

  componentWillLoad() {
    if (typeof this.sx == 'string') {
      this.sx = JSON.parse(this.sx) || {};
    }
    setCSSProps(this.host, {
      ...this.sx,
    });
  }

  componentDidLoad() {
    this.button = this.control.querySelector('button');
  }

  render() {
    return (
      <Host>
        <div 
          class="cbp-accordion-item--control"
          onClick={() => this.handleClick()}
        >
          <cbp-button
            type="button"
            class="cbp-accordion-item--toggle"
            fill="ghost"
            color="secondary"
            controls={`${this.headingId}-content`}
            expanded={`${this.open}`}
            aria-labelledby={this.headingId}
            ref={el => (this.control = el)}
          >
            <cbp-icon name="chevron-right"></cbp-icon>
          </cbp-button>

          <div id={this.headingId} class="cbp-accordion-item--heading">
            <slot name="cbp-accordion-item-label" />
            {this.label && <cbp-typography tag={this.headingLevel} variant="heading-sm">{this.label}</cbp-typography>}
          </div>
        </div>

        <div id={`${this.headingId}-content`} class="cbp-accordion-item--content">
          <slot />
        </div>
      </Host>
    );
  }
}
